import { Component, inject, Input } from '@angular/core';

import { AufnahmePunktDocument, LastModified } from '../../data-model';
import { CheckerComments, commentTimeString, CommentsModalComponent, displayString, ValidationLevel as ValRes } from '@luzula/luzula-lib';
import { ValidationService } from '../validation.service';
import { AuthService } from 'src/app/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProjectService } from 'src/app/project.service';
import { DataService } from 'src/app/data.service';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-plausi',
    templateUrl: './bohrpunkt-plausi.component.html',
    styleUrls: ['./bohrpunkt-plausi.component.css'],
    
})
export class BohrpunktPlausiComponent {
	@Input() bp!: AufnahmePunktDocument;

	ValRes = ValRes;
	displayString = displayString;
	commentTimeString = commentTimeString;

	// Collapse-Status
	messagesCollapsed = false;
	commentsCollapsed = false;

	private modalService = inject(NgbModal);

	constructor(
		private aus: AuthService,
		public ps: ProjectService,
		private ds: DataService,
		public vs: ValidationService) { }

	/**
	 * Prüft ob der Punkt dem aktiven Projekt zugewiesen ist.
	 */
	isAssignedToActiveProject(): boolean {
		const projectId = this.ps.project();
		if (!projectId) return false;
		if (!this.bp?.header?.projects) return false;
		return this.bp.header.projects.some(p => p.id === projectId);
	}

	/**
	 * Prüft ob der Punkt dem Projekt zugeordnet werden kann.
	 */
	get canAssignToProject(): boolean {
		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) return false;
		const hasBearbeiter = projectDoc.bearbeiter && projectDoc.bearbeiter.length > 0;
		if (!hasBearbeiter) return false;
		return this.ps.canAssignToProject();
	}

	/**
	 * Ordnet den Punkt dem aktiven Projekt zu.
	 */
	async assignToProject(): Promise<void> {
		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) return;
		if (!this.bp.header.projects) {
			this.bp.header.projects = [];
		}
		if (this.bp.header.projects.some(p => p.id === projectDoc._id)) return;
		this.bp.header.projects.push({
			id: projectDoc._id,
			name: projectDoc.name
		});
		await this.ds.saveDocument(this.bp);
	}

	/**
	 * Entfernt die Zuordnung des Punktes zum aktiven Projekt.
	 */
	async removeFromProject(): Promise<void> {
		const projectId = this.ps.project();
		if (!projectId) return;
		if (!this.bp.header.projects) return;
		this.bp.header.projects = this.bp.header.projects.filter(p => p.id !== projectId);
		await this.ds.saveDocument(this.bp);
	}

	/**
	 * Gibt die Anzahl der Kommentare zurück.
	 */
	get commentsCount(): number {
		const comments = this.vs.checkerComments();
		return comments ? comments.size : 0;
	}

	/**
	 * Formatiert die letzte Änderung.
	 */
	formatLastModified(lm?: LastModified): string {
		if (!lm) return '';
		const date = new Date(lm.time);
		const dateStr = date.toLocaleDateString('de-DE');
		const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
		return `${dateStr} ${timeStr} von ${lm.user}`;
	}

	/**
	 * Formatiert den Import-Zeitstempel.
	 */
	formatImportTime(timestamp?: number): string {
		if (!timestamp) return '';
		const date = new Date(timestamp);
		const dateStr = date.toLocaleDateString('de-DE');
		const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
		return `${dateStr} ${timeStr}`;
	}

	canComment(cmts: CheckerComments, idx: number) {
		if (!cmts || cmts.resolved || !this.aus.commenting) return false;
		return idx === undefined ? this.aus.commenting : (idx === cmts.comments!.length - 1);
	}

	commentsClass(comments: CheckerComments, prefix = ''): string {
		const rubber = this.aus.pen === 'rubber' ? 'rubber ' : '';
		switch (comments?.type) {
			case ValRes.danger: return `${rubber}${prefix}danger`;
			case ValRes.warning: return `${rubber}${prefix}warning`;
			case ValRes.ok: return `${rubber}${prefix}message`;
			default: return '';
		}
	}

	onCommentsClicked(key: string, value: CheckerComments) {
		const modalRef = this.modalService.open(CommentsModalComponent);
		modalRef.componentInstance.comments = value;
		modalRef.componentInstance.name = key;
		modalRef.closed.subscribe((result?: CheckerComments | 'saved') => {
			// 'saved' = Modal hat bereits gespeichert, nichts mehr tun
			if (result === 'saved') {
				return;
			}
			// Wenn kein Wert zurückgegeben wurde (z.B. bei "Alle löschen"), clearComments aufrufen
			if (!result) {
				this.vs.clearComments(key);
				return;
			}
			// Prüfen ob der Kommentar bereits gelöscht wurde (durch deleteComment in bohrpunkt-comments)
			// In diesem Fall existiert er nicht mehr in der Map und wir sollten nichts tun
			const existing = this.vs.hasComments(key);
			if (!existing) {
				// Kommentar wurde bereits gelöscht - nichts tun
				return;
			}
			// Kommentar existiert noch - aktualisieren
			this.vs.resetComments(key, result);
		})
	}

}
